import { IComputedParameterDef, IParameter, TParameterKey } from '@/pages/Parameters/parameters.types'
import useLastValuesRef, { IUseLastValuesProps } from './useLastValuesRef'
import { useCallback, useMemo } from 'react'
import dataUtils from '../data.utils'

export interface IUseLastValuesWithComputedProps
  extends Omit<IUseLastValuesProps, 'parameters' | 'onNewValueReceived'> {
  parameters: TParameterKey[] // тут и дорасчитываемые, и обычные
  computed?: IComputedParameterDef[]
  onNewValueReceived?: () => void
}

/**
 * Хук получения последних значений с поддержкой вычисляемых параметров.
 *
 * @example
 * const computed = [
 *   {
 *     id: 'computed:carSpeed',
 *     name: 'Скорость кабины',
 *     dependsOn: [carSpeedWholeId, carSpeedFractionalId],
 *     compute: ({ get }) => {
 *       const whole = get(carSpeedWholeId)
 *       const frac = get(carSpeedFractionalId)
 *       return whole == null || frac == null ? null : whole + frac
 *     },
 *   },
 * ]
 *
 * const { ref, loading } = useLastValuesWithComputedRef({
 *   id,
 *   devices: [equipmentId],
 *   live: true,
 *   tsFrom: Date.now() - 5000,
 *   parameters: [carSpeedWholeId, carSpeedFractionalId, 'computed:carSpeed'],
 *   computed,
 * })
 */
const useLastValuesWithComputedRef = (props: IUseLastValuesWithComputedProps) => {
  const parsedToBaseParameterIds: IParameter['id'][] = useMemo(
    () => dataUtils.parsedToBaseParameterIds(props.parameters, props.computed),
    [props.parameters, props.computed]
  )

  const recompute = useCallback(() => {
    if (!props.computed?.length) return
    for (const e of props.devices) {
      if (!lastValuesDataRef.current[e]) continue
      for (const c of props.computed) {
        lastValuesDataRef.current[e][c.id] = c.compute({
          equipmentId: e,
          get: (id) => lastValuesDataRef.current[e][id],
        })
      }
    }
  }, [props.computed, props.devices])

  const handleNewValueReceived = useCallback(() => {
    recompute()
    if (props.onNewValueReceived) props.onNewValueReceived()
  }, [recompute, props.onNewValueReceived])

  const { ref: lastValuesDataRef, loading } = useLastValuesRef({
    ...props,
    parameters: parsedToBaseParameterIds,
    onNewValueReceived: handleNewValueReceived,
  })

  return {
    loading,
    ref: lastValuesDataRef,
  }
}

export default useLastValuesWithComputedRef
